import { isUnmappedPageId } from "@platform/mpx-quotes-grid";
import { BaseBenefitPlanPolicyPeriod } from "@platform/openapi-types";
import { CoverageTypeEnum } from "@platform/types";
import { useCallback } from "react";

import type { IllustratedFromEntry } from "@platform/btx-rates";

const ENROLLMENT = "Enrollment illustrated from";
const VOLUME = "Volume illustrated from";

/**
 * The wording each coverage's marketing-presentation tab puts before the "_"
 * this entry fills, as every broker template spells it. Whole Life's tab has no
 * such row, so its pages get no entry.
 */
const ILLUSTRATED_FROM_LABELS: Partial<Record<CoverageTypeEnum, string>> = {
  [CoverageTypeEnum.DENTAL]: ENROLLMENT,
  [CoverageTypeEnum.DENTAL_PPO]: ENROLLMENT,
  [CoverageTypeEnum.DENTAL_HMO]: ENROLLMENT,
  [CoverageTypeEnum.VISION]: ENROLLMENT,
  [CoverageTypeEnum.FMLA]: ENROLLMENT,
  [CoverageTypeEnum.EAP]: ENROLLMENT,
  [CoverageTypeEnum.STD]: "Covered Benefit illustrated from",
  [CoverageTypeEnum.LTD]: "Covered Payroll illustrated from",
  [CoverageTypeEnum.BASIC_LIFE_ADND]: VOLUME,
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: VOLUME,
  [CoverageTypeEnum.ACCIDENT]: VOLUME,
  [CoverageTypeEnum.CRITICAL_ILLNESS]: VOLUME,
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: VOLUME,
  [CoverageTypeEnum.CANCER]: VOLUME,
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: VOLUME,
};

type IllustratedFromPage = {
  id: string;
  illustratedFrom?: string;
  policyPeriod?: BaseBenefitPlanPolicyPeriod;
  position: number;
};

type UseIllustratedFromProps = {
  selectedCoverageType?: CoverageTypeEnum;
  selectedBpgId?: string;
  selectedBenefitPlanGroup?: IllustratedFromPage;
  /** The selected coverage's own SBS pages, without the Unmapped Plans page. */
  coverageBenefitPlanGroups: IllustratedFromPage[];
  saveIllustratedFrom: (groupId: string, illustratedFrom: string) => void;
};

const isCurrentPage = (page: IllustratedFromPage): boolean =>
  page.policyPeriod === BaseBenefitPlanPolicyPeriod.current;

const isAlternatePage = (page: IllustratedFromPage): boolean =>
  page.policyPeriod === BaseBenefitPlanPolicyPeriod.alternate;

const hasEntry = (page: IllustratedFromPage): boolean =>
  !!page.illustratedFrom?.trim();

/** Current pages first, then by position. */
const byMainTabRank = (
  a: IllustratedFromPage,
  b: IllustratedFromPage,
): number =>
  Number(isCurrentPage(b)) - Number(isCurrentPage(a)) ||
  a.position - b.position;

/**
 * The page whose entry the coverage's main tab prints. Every non-alternate
 * page's columns share that tab, which has one "illustrated from" row, and the
 * export fills it with the current page's entry, else the first filled page's
 * by position (mainTabIllustratedFrom in centro-portal's marketing-presentation
 * composer), so the two rules change together. With no entry anywhere, the
 * first page in that order is the one an edit should fill.
 */
const findMainTabEntryPage = (
  pages: IllustratedFromPage[],
): IllustratedFromPage | undefined => {
  const mainTabPages = pages
    .filter((page) => !isAlternatePage(page))
    .sort(byMainTabRank);

  return mainTabPages.find(hasEntry) ?? mainTabPages[0];
};

/**
 * Resolves the selected SBS page's "illustrated from" entry: the source of the
 * enrollment or volume the page's rates are illustrated with, which the
 * marketing presentation prints in the tab's "... illustrated from _" row.
 *
 * The entry belongs to the page's RFP benefit plan group and is read and
 * written by that group's own id. The Unmapped Plans page has no group of its
 * own. The export prints its plans under the main tab's entry, on the main tab
 * or, for carrier groups BTX ranks as alternates, on "Alt" tabs of their own.
 * So the page shows that entry, and edits the page it comes from.
 */
export const useIllustratedFrom = ({
  selectedCoverageType,
  selectedBpgId,
  selectedBenefitPlanGroup,
  coverageBenefitPlanGroups,
  saveIllustratedFrom,
}: UseIllustratedFromProps): IllustratedFromEntry | undefined => {
  const label = selectedCoverageType
    ? ILLUSTRATED_FROM_LABELS[selectedCoverageType]
    : undefined;
  const tabPage = isUnmappedPageId(selectedBpgId)
    ? findMainTabEntryPage(coverageBenefitPlanGroups)
    : selectedBenefitPlanGroup;
  const groupId = tabPage?.id;

  const handleIllustratedFromChange = useCallback(
    (illustratedFrom: string) => {
      if (!groupId) return;
      saveIllustratedFrom(groupId, illustratedFrom.trim());
    },
    [groupId, saveIllustratedFrom],
  );

  if (!label || !groupId) return undefined;

  return {
    label,
    value: tabPage?.illustratedFrom ?? "",
    onChange: handleIllustratedFromChange,
  };
};
